import { FC, useRef, InputHTMLAttributes, useCallback, useLayoutEffect, useMemo, useEffect } from 'react'
import IMask from 'imask'
import { Omit } from 'interfaces'
import { useFormContext, Controller } from 'react-hook-form'
import { usePrev } from 'hooks/usePrev'

/*
  WARNING: MaskedInput don't follow `options` prop changes
*/

export type MaskOptions = IMask.AnyMaskedOptions

export type MaskedInputProps =
  & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'defaultValue'>
  & {
    value: string
    options?: MaskOptions
    renderer?: FC<InputHTMLAttributes<HTMLInputElement> & { innerRef?: (ref: HTMLInputElement | null) => void }>
    onChange: (value: string) => void
    onFocus?: () => void
    autoFocusOn?: boolean // Focus this field when true
  }

export const MaskedInput: FC<MaskedInputProps> = (props) => {
  const {
    options,
    value,
    renderer,
    onChange,
    autoFocusOn = false,
    ...restInputProps
  } = props

  const ref = useRef<HTMLInputElement>()
  const maskRef = useRef<IMask.InputMask<IMask.AnyMaskedOptions>>()
  const onChangeRef = useRef(onChange)
  const localValueRef = useRef(value)

  const outsideChangeTriggerRef = useRef(0)
  useMemo(() => {
    if (value !== localValueRef.current) {
      outsideChangeTriggerRef.current++
      localValueRef.current = value
    }
  }, [value])

  const prevAutoFocusOn = usePrev(autoFocusOn)
  useEffect(() => {
    const { current } = ref
    if (!current || !autoFocusOn) {
      return
    }

    if (!!prevAutoFocusOn !== autoFocusOn) {
      current.focus()
    }

  }, [autoFocusOn, prevAutoFocusOn])

  useLayoutEffect(() => {
    const { current } = ref
    if (!current) {
      return
    }

    current.value = value
    if (!options) {
      return
    }

    const mask = maskRef.current = IMask(current, options)
    mask.on('accept', () => {
      const currentValue = mask.unmaskedValue
      localValueRef.current = currentValue
      onChangeRef.current(currentValue)
    })

    return () => {
      maskRef.current = undefined
      mask.destroy()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outsideChangeTriggerRef.current]) // This effect should be re rendered for outside changes only! Avoid `value` and other depts!

  const handleChange = useCallback(() => {}, [])

  // WARNING: Avoid re render for this useMemo!!!
  return useMemo(() => {
    const handleRef = (input: HTMLInputElement) => {
      ref.current = input
    }

    const {
      inputMode = 'numeric',
      ...restProps
    } = restInputProps

    const commonProps = {
      inputMode,
      defaultValue: value,
      onChange: handleChange,
      autoComplete: 'new-password', // Prevents autocompletion better than 'off'
      'data-numeric-input': '',
      'data-lpignore': 'true', // Prevent LastPass from treating field
    }

    if (!renderer) {
      return (
        <input
          { ...commonProps }
          { ...restProps }
          ref={ handleRef }
        />
      )
    }

    const Input = renderer
    return (
      <Input
        { ...commonProps }
        { ...restProps }
        innerRef={ handleRef }
      />
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restInputProps.className])  // Avoid re render input on value changes
}

export type MaskedInputControllerProps =
  & Omit<MaskedInputProps, 'name' | 'value' | 'onChange'>
  & {
    name: string
  }
export const MaskedInputController: FC<MaskedInputControllerProps> = (props) => {
  const { name, ...restProps } = props

  const { control, trigger } = useFormContext()

  const inputRenderer = useCallback(({ field: { value, onChange }}) => {
    const handleChange = (nextValue: string) => {
      onChange(nextValue)
      trigger(name)
    }
    return (
      <MaskedInput name={ name } value={ value } onChange={ handleChange } { ...restProps } />
    )
  }, [restProps, name, trigger])

  return (
    <Controller
      control={ control }
      name={ name }
      render={ inputRenderer }
    />
  )
}
